import {getFieldBySlug} from '@packages/systems/dynamo/utils/CollectionUtils';
import {
  isImageRefSet,
  DEFAULT_FIELD_SLUGS_FOR_COLLECTIONS_BY_SECTION,
} from '@packages/systems/dynamo/utils/CollectionFieldUtils';
import {fieldSlug as formatFieldSlug} from '@packages/systems/dynamo/utils/SlugUtils';

import {DEFAULT_SKU_SLUG} from '@packages/systems/commerce/constants';
import {
  getProductCollection,
  getCategoriesCollection,
  getSkuCollection,
  isProductsCollection,
  isSkusCollection,
} from '@packages/systems/commerce/utils/CommerceCollectionUtils';

import {
  getBindableFields,
  defineBindablePropertyFromField,
  defineBindablePropertyFromFieldPath,
  toBindingPath,
  defineCollectionTypeReference,
  sortFields,
  type FieldPath,
} from '@packages/systems/cms/gateway/shared';

import {MORE_IMAGES_BINDING_CONTEXT_TYPE_ID} from './identifiers';
import {
  groupCollectionFieldsByProductSection,
  getBindableSkuFields,
  sortProductAndSkuFields,
} from './products-and-skus';
import {cartBindableProperties} from './cart';
import {productOptionBindableProperties} from './product-options';
import {orderItemBindableProperties} from './order-items';
import {addToCartBindableProperties} from './addToCart';
import {orderBindableProperties} from './order';
import {shippingMethodBindableProperties} from './shipping-methods';
import {extraItemBindableProperties} from './extra-items';
import {downloadBindableProperties} from './downloads';

import type {
  Collections,
  CollectionRecordType as Collection,
} from '@packages/systems/dynamo/records/CollectionRecord';
import type {FieldType as Field} from '@packages/systems/dynamo/records/Field';
import type {
  BindablePropertiesDef,
  BindablePropertyDef,
  TypeReferenceBuilder,
} from '@packages/systems/dynamo/source-types';
import {COLLECTION_TYPES} from '@packages/systems/dynamo/constants';

export default function defineBindableProperties<
  RootTRef,
  EntityTRef,
  ListTRef,
>(
  collections: Collections,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>,
  isDesigner: boolean
): BindablePropertiesDef<EntityTRef, ListTRef>[] {
  const products = getProductCollection(collections);
  const categories = getCategoriesCollection(collections);
  const skus = getSkuCollection(collections);

  const propertiesDefs = [
    cartBindableProperties(referenceBuilder),
    productOptionBindableProperties(referenceBuilder),
    ...orderBindableProperties(referenceBuilder),
    shippingMethodBindableProperties(referenceBuilder),
    extraItemBindableProperties(referenceBuilder),
    downloadBindableProperties(referenceBuilder),
  ];

  if (products) {
    propertiesDefs.push(
      defineProductBindableProperties(
        products,
        skus,
        referenceBuilder,
        isDesigner
      ),
      ...defineBindablePropertiesForMultiImageFields(
        products,
        referenceBuilder
      ),
      addToCartBindableProperties(referenceBuilder)
    );
  }

  if (products && skus) {
    propertiesDefs.push(
      orderItemBindableProperties(products, skus, referenceBuilder, isDesigner)
    );
  }

  if (categories) {
    propertiesDefs.push(
      defineCategoryBindableProperties(
        categories,
        referenceBuilder,
        isDesigner
      ),
      ...defineBindablePropertiesForMultiImageFields(
        categories,
        referenceBuilder
      )
    );
  }

  const moreImagesField = skus ? getFieldBySlug(skus, 'more-images') : null;
  const defaultSkuField = products
    ? getFieldBySlug(products, DEFAULT_SKU_SLUG)
    : null;
  if (skus && defaultSkuField && moreImagesField) {
    propertiesDefs.push(
      defineMultiImageFieldBindableProperties(
        moreImagesField,
        defaultSkuField,
        skus,
        referenceBuilder
      )
    );
  }

  return propertiesDefs;
}

function defineProductBindableProperties<RootTRef, EntityTRef, ListTRef>(
  products: Collection,
  skus: Collection | null | undefined,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>,
  isDesigner: boolean
): BindablePropertiesDef<EntityTRef, ListTRef> {
  const productsTypeRef = referenceBuilder.entityOf(
    defineCollectionTypeReference(products)
  );

  let productsBindableFields = getBindableFields(products);
  if (isDesigner) {
    // sort fields only in Designer environment for UI reasons
    productsBindableFields = sortProductAndSkuFields(productsBindableFields);
  }

  const skusBindableFields = skus
    ? sortProductAndSkuFields(getBindableSkuFields(skus))
    : [];

  const defaultSkuField = getFieldBySlug(products, DEFAULT_SKU_SLUG);

  const groupedProductFields = groupCollectionFieldsByProductSection(
    productsBindableFields,
    products
  );
  const groupedSkusFields = groupCollectionFieldsByProductSection(
    skusBindableFields,
    skus
  );

  const groupLabels = [
    ...Object.keys(
      DEFAULT_FIELD_SLUGS_FOR_COLLECTIONS_BY_SECTION[COLLECTION_TYPES.PRODUCTS]
    ),
    'Custom fields',
    'Other',
  ];

  const bindableProperties = groupLabels.reduce<
    BindablePropertyDef<EntityTRef, ListTRef>[]
  >((props, groupLabel) => {
    const productFieldGroup = groupedProductFields[groupLabel];
    const skuFieldGroup = groupedSkusFields[groupLabel];

    if (productFieldGroup) {
      productFieldGroup.forEach((field) => {
        const bindableProp = defineBindablePropertyFromField<
          RootTRef,
          EntityTRef,
          ListTRef
        >({collection: products, field, referenceBuilder, groupLabel});

        if (bindableProp) {
          props.push(bindableProp);
        }
      });
    }

    if (skuFieldGroup && defaultSkuField) {
      skuFieldGroup.forEach((field) => {
        const fieldPath: FieldPath = [defaultSkuField, field];

        const bindingSource = defineBindablePropertyFromFieldPath<
          RootTRef,
          EntityTRef,
          ListTRef
        >(
          field.slug === 'ecSkuInventoryQuantity'
            ? {
                collection: skus,
                fieldPath,
                referenceBuilder,
                groupLabel,
                renderingStrategy: 'client',
                queryPath: [
                  `${formatFieldSlug(defaultSkuField.slug)}: resolveLocalItemRef(slug: "default-sku")`,
                  'ecSkuInventoryQuantity: fieldCommerceInventoryQuantity',
                ],
              }
            : {collection: skus, fieldPath, referenceBuilder, groupLabel}
        );

        if (bindingSource) {
          props.push(bindingSource);
        }
      });
    }

    return props;
  }, []);

  bindableProperties.push({
    id: 'product-sku-properties',
    label: '',
    type: 'CommercePropTable',
    path: [
      {
        slug: 'sku-properties',
        type: 'CommercePropTable',
      },
    ],
    queryPath: [
      `${formatFieldSlug('sku-properties')}: fieldCommercePropTable(slug: "sku-properties")`,
    ],
    meta: {required: false},
  });

  bindableProperties.push({
    id: 'product-sku-values',
    label: '',
    type: 'CommercePropValues',
    path: [
      {
        slug: 'default-sku',
        type: 'ItemRef',
      },
      {
        slug: 'sku-values',
        type: 'CommercePropValues',
      },
    ],
    queryPath: [
      `${formatFieldSlug('default-sku')}: resolveLocalItemRef(slug: "default-sku")`,
      `${formatFieldSlug('sku-values')}: fieldCommercePropValues(slug: "sku-values")`,
    ],
    meta: {required: false},
  });
  bindableProperties.push({
    id: 'product-full-slug',
    type: 'FullSlug',
    label: 'URL Path',
    path: [{slug: 'fullSlug', type: 'FullSlug'}],
    queryPath: ['fullSlug'],
    meta: {
      required: false,
    },
  });

  bindableProperties.push({
    id: 'no-search',
    type: 'Bool',
    label: 'NoSearch',
    // this is the binding path we need for a binding
    path: [{slug: 'noSearch', type: 'Bool'}],
    // this needs to match the field name in the GraphQL schema
    queryPath: ['noSearch'],
    // !IMPORTANT! noSearch is used only in query generation and
    // and should not be available as a binding option, so we mark
    // it as "deprecated" even though it is not deprecated
    deprecated: true,
    meta: {
      required: false,
    },
  });

  return {type: productsTypeRef, bindableProperties};
}

function defineCategoryBindableProperties<RootTRef, EntityTRef, ListTRef>(
  categories: Collection,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>,
  isDesigner: boolean
): BindablePropertiesDef<EntityTRef, ListTRef> {
  let bindableFields = getBindableFields(categories);
  if (isDesigner) {
    // sort fields only in Designer environment for UI reasons
    bindableFields = sortFields(bindableFields);
  }
  const bindableProperties = bindableFields.reduce<Array<any>>(
    (props, field) => {
      const bindableProp = defineBindablePropertyFromField<
        RootTRef,
        EntityTRef,
        ListTRef
      >({collection: categories, field, referenceBuilder});
      if (bindableProp) {
        props.push(bindableProp);
      }
      return props;
    },
    []
  );

  bindableProperties.push({
    id: 'category-full-slug',
    type: 'FullSlug',
    label: 'URL Path',
    path: [{slug: 'fullSlug', type: 'FullSlug'}],
    queryPath: ['fullSlug'],
    meta: {
      required: false,
    },
  });

  bindableProperties.push({
    id: 'no-search',
    type: 'Bool',
    label: 'NoSearch',
    // this is the binding path we need for a binding
    path: [{slug: 'noSearch', type: 'Bool'}],
    // this needs to match the field name in the GraphQL schema
    queryPath: ['noSearch'],
    // !IMPORTANT! noSearch is used only in query generation and
    // and should not be available as a binding option, so we mark
    // it as "deprecated" even though it is not deprecated
    deprecated: true,
    meta: {
      required: false,
    },
  });

  return {
    type: referenceBuilder.entityOf(defineCollectionTypeReference(categories)),
    bindableProperties,
  };
}

function defineBindablePropertiesForMultiImageFields<
  RootTRef,
  EntityTRef,
  ListTRef,
>(
  collection: Collection,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindablePropertiesDef<EntityTRef, ListTRef>[] {
  return collection.fields
    .toArray()
    .filter(isImageRefSet)
    .map((field) =>
      defineMultiImageFieldBindableProperties(
        field,
        null,
        collection,
        referenceBuilder
      )
    );
}

function getProductSectionNameForField(
  field: Field,
  collection: Collection
): string {
  const productSectionGroups = groupCollectionFieldsByProductSection(
    [field],
    collection
  );
  const productSectionNames = Object.keys(productSectionGroups);
  const productSectionName = productSectionNames.find((sectionName) => {
    const productSectionGroup = productSectionGroups[sectionName];
    // @ts-expect-error - TS18048 - 'productSectionGroup' is possibly 'undefined'.
    return productSectionGroup.some((groupField) => groupField === field);
  });
  return productSectionName || '';
}

function defineMultiImageFieldBindableProperties<
  RootTRef,
  EntityTRef,
  ListTRef,
>(
  field: Field,
  defaultSkuField: Field | null | undefined,
  collection: Collection,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindablePropertiesDef<EntityTRef, ListTRef> {
  // TODO: Address field id uniqueness
  const typeId = defaultSkuField
    ? MORE_IMAGES_BINDING_CONTEXT_TYPE_ID
    : field.id;

  const fieldPath: FieldPath = defaultSkuField
    ? [defaultSkuField, field]
    : [field];

  const groupLabel =
    isProductsCollection(collection) || isSkusCollection(collection)
      ? getProductSectionNameForField(field, collection)
      : '';

  const bindablePropDef: BindablePropertyDef<EntityTRef, ListTRef> = {
    id: field.id,
    type: 'ImageRef',
    label: field.name,
    path: toBindingPath(fieldPath),
    queryPath: [],
    meta: {
      required: field.required,
    },
  };

  if (groupLabel) {
    bindablePropDef.groupLabel = groupLabel;
  }

  return {
    type: referenceBuilder.entityOf({
      entity: 'Field',
      id: typeId,
    }),
    bindableProperties: [bindablePropDef],
  };
}
